export interface SpeakOptions {
  lang?: string;
  rate?: number;
  voiceName?: string | null;
}

export function ttsSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function listVoices(): SpeechSynthesisVoice[] {
  if (!ttsSupported()) return [];
  try {
    return window.speechSynthesis.getVoices();
  } catch {
    return [];
  }
}

/** Some browsers populate the voice list asynchronously. */
export function onVoicesChanged(callback: () => void): () => void {
  if (!ttsSupported()) return () => {};
  const synth = window.speechSynthesis;
  synth.addEventListener?.('voiceschanged', callback);
  return () => synth.removeEventListener?.('voiceschanged', callback);
}

export function stopSpeaking() {
  if (!ttsSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    // ignore
  }
}

/** Speak `text`; resolves when finished (or immediately when unsupported). */
export function speak(text: string, options: SpeakOptions = {}): Promise<void> {
  if (!ttsSupported() || !text.trim()) return Promise.resolve();
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const lang = options.lang ?? 'de';
    utterance.lang = lang.length === 2 ? (lang === 'de' ? 'de-DE' : 'en-US') : lang;
    utterance.rate = Math.min(2, Math.max(0.5, options.rate ?? 1));
    if (options.voiceName) {
      const voice = listVoices().find((v) => v.name === options.voiceName);
      if (voice) utterance.voice = voice;
    }
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    try {
      synth.speak(utterance);
    } catch {
      resolve();
    }
  });
}
